import { useRef, useEffect } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { RevealLines } from "./Reveal";
import { MysteryCountdown } from "./MysteryCountdown";
import { useLang } from "./LanguageContext";

const HERO_VIDEO = "/stage/hero-carlo.mp4";

const PLATED_IMG =
  "https://static.prod-images.emergentagent.com/jobs/6918af59-ab46-445b-a012-e513807d76c0/images/50a0dee36351ed7c7028984a02e0aea40cdadd536c63bc50880f8d204c79d74d.jpeg";

const copy = {
  en: {
    badge: "By Invitation · A Living Craft",
    overline: "Live Mozzarella Experience · Crafted by Hand",
    h1a: "The art of",
    h1b: "mozzarella,",
    h1c: "made live.",
    sub: "An exclusive tableside ritual for the world's most discerning venues. Fresh mozzarella, drawn by hand and presented live — where craft becomes theatre and every guest becomes a witness to the making.",
    cta1: "Request a Presentation",
    cta2: "Discover the craft",
    card: "Bespoke",
    cardSub: "composed for your venue",
    plated: "…to your plate.",
  },
  it: {
    badge: "Su Invito · Un Mestiere Vivo",
    overline: "Live Mozzarella Experience · Fatta a Mano",
    h1a: "L'arte della",
    h1b: "mozzarella,",
    h1c: "dal vivo.",
    sub: "Un rituale esclusivo al tavolo per le sale più raffinate del mondo. Mozzarella fresca, filata a mano e presentata dal vivo — dove il mestiere diventa teatro e ogni ospite è testimone della creazione.",
    cta1: "Richiedi una Presentazione",
    cta2: "Scopri il mestiere",
    card: "Su Misura",
    cardSub: "composto per la tua sala",
    plated: "…nel tuo piatto.",
  },
  ru: {
    badge: "Только по приглашению · Живое ремесло",
    overline: "Live Mozzarella Experience · Ручная работа",
    h1a: "Искусство",
    h1b: "моцареллы,",
    h1c: "вживую.",
    sub: "Эксклюзивный ритуал у вашего стола для самых взыскательных заведений мира. Свежая моцарелла, вытянутая вручную и приготовленная на глазах у гостей — там, где ремесло становится театром.",
    cta1: "Запросить презентацию",
    cta2: "Узнать о ремесле",
    card: "Индивидуально",
    cardSub: "создано для вашего заведения",
    plated: "…на вашей тарелке.",
  },
  es: {
    badge: "Solo con Invitación · Un Oficio Vivo",
    overline: "Live Mozzarella Experience · Hecha a Mano",
    h1a: "El arte de la",
    h1b: "mozzarella,",
    h1c: "en vivo.",
    sub: "Un ritual exclusivo junto a la mesa para los salones más exigentes del mundo. Mozzarella fresca, hilada a mano y presentada en vivo — donde el oficio se convierte en teatro y cada invitado es testigo de la creación.",
    cta1: "Solicitar una Presentación",
    cta2: "Descubre el oficio",
    card: "A Medida",
    cardSub: "compuesto para tu salón",
    plated: "…a tu plato.",
  },
};

export const Hero = ({ countdown = true }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "24%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const { lang } = useLang();
  const t = copy[lang];
  const videoRef = useRef(null);

  useEffect(() => {
    videoRef.current?.play().catch(() => {});
  }, []);

  return (
    <section ref={ref} className="relative min-h-[100svh] pt-28 pb-16 overflow-hidden" data-testid="hero-section">
      <div aria-hidden="true" className="pointer-events-none absolute -top-40 -right-32 w-[620px] h-[620px] rounded-full bg-terracotta/10 blur-3xl" />
      <div aria-hidden="true" className="pointer-events-none absolute bottom-0 -left-40 w-[520px] h-[520px] rounded-full bg-olive/10 blur-3xl" />
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center relative z-10">
        <div className="lg:col-span-7 relative z-20">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25, duration: 0.8 }}
            className="inline-flex items-center gap-2 rounded-full border border-terracotta/40 bg-terracotta/10 px-4 py-1.5 mb-6"
            data-testid="hero-fall-promo"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-terracotta animate-pulse" />
            <span className="font-sans text-[11px] md:text-xs uppercase tracking-[0.28em] text-terracotta">
              {t.badge}
            </span>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4, duration: 1 }}
            className="font-sans text-xs md:text-sm uppercase tracking-[0.35em] text-ink-soft mb-6 md:mb-8"
            data-testid="hero-overline"
          >
            {t.overline}
          </motion.p>

          <h1 key={lang} className="font-serif text-ink text-5xl md:text-7xl lg:text-8xl leading-[0.9] tracking-tight">
            <RevealLines lines={[t.h1a]} delay={0.3} />
            <RevealLines lines={[t.h1b]} delay={0.42} className="text-ink-soft" />
            <RevealLines lines={[<em key="s" className="italic pr-2">{t.h1c}</em>]} delay={0.54} />
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.55, duration: 0.8 }}
            className="font-sans text-base md:text-lg text-ink-soft max-w-md mt-8 md:mt-10 leading-relaxed"
            data-testid="hero-subtitle"
          >
            {t.sub}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.7 }}
            className="mt-8 md:mt-10"
          >
            {countdown && <MysteryCountdown />}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.85, duration: 0.8 }}
            className="mt-10 flex flex-wrap items-center gap-5"
          >
            <button
              data-testid="hero-book-button"
              onClick={() => document.getElementById("book")?.scrollIntoView({ behavior: "smooth" })}
              className="group relative overflow-hidden rounded-full bg-terracotta px-8 py-4 text-sm font-sans tracking-wide text-white"
            >
              <span className="relative z-10">{t.cta1}</span>
              <span className="absolute inset-0 bg-ink scale-x-0 group-hover:scale-x-100 origin-left transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]" />
            </button>
            <button
              data-testid="hero-craft-button"
              onClick={() => document.getElementById("craft")?.scrollIntoView({ behavior: "smooth" })}
              className="font-sans text-sm tracking-wide text-ink underline underline-offset-4 decoration-ink/30 hover:decoration-ink transition-colors"
            >
              {t.cta2}
            </button>
          </motion.div>
        </div>

        <div className="lg:col-span-5 relative">
          <motion.div
            initial={{ clipPath: "inset(100% 0 0 0)" }}
            animate={{ clipPath: "inset(0% 0 0 0)" }}
            transition={{ duration: 1.3, ease: [0.22, 1, 0.36, 1], delay: 0.5 }}
            className="relative aspect-[3/4] overflow-hidden rounded-sm shadow-[0_30px_60px_-20px_rgba(28,25,23,0.25)]"
            data-testid="hero-image"
          >
            <motion.video
              ref={videoRef}
              src={HERO_VIDEO}
              poster="/stage/poster-carlo.jpg"
              autoPlay
              muted
              loop
              playsInline
              preload="auto"
              style={{ y: imgY, scale }}
              className="absolute inset-0 w-full h-[120%] object-cover"
              data-testid="hero-video"
            />
            <div className="absolute inset-x-0 bottom-0 h-[22%] bg-gradient-to-t from-[#120a0c]/90 via-[#120a0c]/40 to-transparent pointer-events-none" data-testid="hero-video-fade" />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.95, duration: 0.8 }}
            className="absolute -bottom-6 -left-6 md:-left-10 bg-bg border border-ink/10 rounded-sm px-6 py-4 shadow-lg"
          >
            <p className="font-serif italic text-3xl md:text-4xl text-ink leading-none">{t.card}</p>
            <p className="font-sans text-[11px] uppercase tracking-[0.2em] text-ink-soft mt-1">{t.cardSub}</p>
          </motion.div>

          <motion.figure
            initial={{ opacity: 0, scale: 0.9, y: 24 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ delay: 1.0, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="absolute -bottom-8 -right-4 md:-right-8 w-36 md:w-56 rounded-sm overflow-hidden border-[5px] border-bg shadow-[0_24px_50px_-18px_rgba(28,25,23,0.55)] z-20 hidden md:block"
            data-testid="hero-plated"
          >
            <img
              src={PLATED_IMG}
              alt="Plated burrata that arrives at your table"
              className="w-full aspect-[4/3] object-cover"
            />
            <figcaption className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-ink/80 to-transparent px-3 pt-8 pb-2">
              <span className="font-serif italic text-bg text-sm md:text-base">{t.plated}</span>
            </figcaption>
          </motion.figure>
        </div>
      </div>
    </section>
  );
};
